import { supabase } from './supabaseClient';

/**
 * Supabase implementation of access code service
 * This is a temporary solution - can be replaced with company database implementation
 */

/**
 * Validate an access code
 */
export const validateAccessCode = async (code) => {
  if (!supabase) {
    // Fallback to hardcoded validation if Supabase not configured
    return { valid: false, expired: false };
  }

  try {
    const lowerCode = code.toLowerCase();
    
    // Query for the access code (case-insensitive)
    // Note: We need to get all active codes and filter client-side for case-insensitive match
    // Supabase ilike requires a pattern, so we'll fetch and filter
    const { data: allCodes, error } = await supabase
      .from('access_codes')
      .select('*')
      .eq('is_active', true);
    
    if (error) {
      return { valid: false, expired: false };
    }
    
    // Find case-insensitive match
    const matched = allCodes?.find(ac => ac.code.toLowerCase() === lowerCode);
    
    if (!matched) {
      return { valid: false, expired: false };
    }
    
    const data = matched;

    // Check expiration
    if (data.expires_at) {
      const expirationDate = new Date(data.expires_at);
      const today = new Date();
      today.setHours(0, 0, 0, 0);

      if (expirationDate < today) {
        return {
          valid: false,
          expired: true,
          expirationDate: data.expires_at,
        };
      }
    }

    return {
      valid: true,
      expired: false,
      code: data.code,
      maxValuations: data.max_valuations || undefined,
    };
  } catch (error) {
    console.error('Error validating access code:', error);
    return { valid: false, expired: false };
  }
};

/**
 * Get all access codes
 */
export const getAllAccessCodes = async (options = {}) => {
  if (!supabase) {
    return [];
  }

  try {
    let query = supabase
      .from('access_codes')
      .select('*')
      .order('created_at', { ascending: false });

    if (!options.includeInactive) {
      query = query.eq('is_active', true);
    }

    const { data, error } = await query;

    if (error) {
      console.error('Error fetching access codes:', error);
      return [];
    }

    return data || [];
  } catch (error) {
    console.error('Error fetching access codes:', error);
    return [];
  }
};

/**
 * Get access code by ID
 */
export const getAccessCodeById = async (id) => {
  if (!supabase) {
    return null;
  }

  try {
    const { data, error } = await supabase
      .from('access_codes')
      .select('*')
      .eq('id', id)
      .single();

    if (error || !data) {
      return null;
    }

    return data;
  } catch (error) {
    console.error('Error fetching access code:', error);
    return null;
  }
};

/**
 * Get access code by code string
 */
export const getAccessCodeByCode = async (code) => {
  if (!supabase) {
    return null;
  }

  try {
    // Get all codes and filter client-side for case-insensitive match
    const { data: allCodes, error } = await supabase
      .from('access_codes')
      .select('*');
    
    if (error) {
      return null;
    }
    
    const matched = allCodes?.find(ac => ac.code.toLowerCase() === code.toLowerCase());
    
    if (!matched) {
      return null;
    }
    
    const data = matched;

    if (error || !data) {
      return null;
    }

    return data;
  } catch (error) {
    console.error('Error fetching access code:', error);
    return null;
  }
};

/**
 * Create a new access code
 */
export const createAccessCode = async (data) => {
  if (!supabase) {
    throw new Error('Supabase not configured');
  }

  try {
    const { data: newCode, error } = await supabase
      .from('access_codes')
      .insert([
        {
          code: data.code,
          expires_at: data.expires_at || null,
          max_valuations: data.max_valuations || null,
          is_active: data.is_active !== undefined ? data.is_active : true,
          notes: data.notes || null,
        },
      ])
      .select()
      .single();

    if (error) {
      throw error;
    }

    return newCode;
  } catch (error) {
    console.error('Error creating access code:', error);
    throw error;
  }
};

/**
 * Update an access code
 */
export const updateAccessCode = async (id, data) => {
  if (!supabase) {
    throw new Error('Supabase not configured');
  }

  try {
    const updateData = {};
    if (data.code !== undefined) updateData.code = data.code;
    if (data.expires_at !== undefined) updateData.expires_at = data.expires_at || null;
    if (data.max_valuations !== undefined) updateData.max_valuations = data.max_valuations || null;
    if (data.is_active !== undefined) updateData.is_active = data.is_active;
    if (data.notes !== undefined) updateData.notes = data.notes || null;

    const { data: updatedCode, error } = await supabase
      .from('access_codes')
      .update(updateData)
      .eq('id', id)
      .select()
      .single();

    if (error) {
      throw error;
    }

    return updatedCode;
  } catch (error) {
    console.error('Error updating access code:', error);
    throw error;
  }
};

/**
 * Delete an access code
 */
export const deleteAccessCode = async (id) => {
  if (!supabase) {
    throw new Error('Supabase not configured');
  }

  try {
    const { error } = await supabase
      .from('access_codes')
      .delete()
      .eq('id', id);

    if (error) {
      throw error;
    }
  } catch (error) {
    console.error('Error deleting access code:', error);
    throw error;
  }
};

/**
 * Get usage count for an access code
 */
export const getValuationUsage = async (code) => {
  if (!supabase) {
    // Fallback to localStorage if Supabase not configured
    try {
      const stored = localStorage.getItem(`demo_valuation_usage_${code.toLowerCase()}`);
      return stored ? parseInt(stored, 10) : 0;
    } catch (error) {
      return 0;
    }
  }

  try {
    // First get the access code to find its ID
    const accessCode = await getAccessCodeByCode(code);
    if (!accessCode) {
      return 0;
    }

    const { count, error } = await supabase
      .from('access_code_usage')
      .select('*', { count: 'exact', head: true })
      .eq('access_code_id', accessCode.id);

    if (error) {
      console.error('Error getting usage count:', error);
      return 0;
    }

    return count || 0;
  } catch (error) {
    console.error('Error getting usage count:', error);
    return 0;
  }
};

/**
 * Increment usage count for an access code
 */
export const incrementValuationUsage = async (code, metadata = {}) => {
  if (!supabase) {
    // Fallback to localStorage if Supabase not configured
    try {
      const current = await getValuationUsage(code);
      const newCount = current + 1;
      localStorage.setItem(`demo_valuation_usage_${code.toLowerCase()}`, newCount.toString());
      return newCount;
    } catch (error) {
      throw error;
    }
  }

  try {
    // First get the access code to find its ID
    const accessCode = await getAccessCodeByCode(code);
    if (!accessCode) {
      throw new Error('Access code not found');
    }

    // Insert usage record
    const { error } = await supabase
      .from('access_code_usage')
      .insert([
        {
          access_code_id: accessCode.id,
          access_code: code, // Store the actual access code string
          patent_number: metadata.patent_number || null,
          company_name: metadata.company_name || null,
        },
      ]);

    if (error) {
      throw error;
    }

    // Return new count
    return await getValuationUsage(code);
  } catch (error) {
    console.error('Error incrementing usage:', error);
    throw error;
  }
};

/**
 * Get usage records for an access code
 */
export const getUsageRecords = async (code, options = {}) => {
  if (!supabase) {
    return [];
  }

  try {
    const accessCode = await getAccessCodeByCode(code);
    if (!accessCode) {
      return [];
    }

    let query = supabase
      .from('access_code_usage')
      .select('*')
      .eq('access_code_id', accessCode.id)
      .order('used_at', { ascending: false });

    if (options.limit) {
      query = query.limit(options.limit);
    }

    const { data, error } = await query;

    if (error) {
      console.error('Error fetching usage records:', error);
      return [];
    }

    return data || [];
  } catch (error) {
    console.error('Error fetching usage records:', error);
    return [];
  }
};

// Export all functions as default object
export default {
  validateAccessCode,
  getAllAccessCodes,
  getAccessCodeById,
  getAccessCodeByCode,
  createAccessCode,
  updateAccessCode,
  deleteAccessCode,
  getValuationUsage,
  incrementValuationUsage,
  getUsageRecords,
};

